import { ActionSlot, AgentIcon, getLobbyGameParams } from "~/business";
import { useActions, useGraphicScale } from "~/jsx/hook";
import { Spacing } from "~/jsx/theme";
import { partition, sortBy } from "~/utils";
import { XActionSlot, XPackBy, XThroneSvg } from "../design";
import { XActionSlotGroup } from "./XActionSlotGroup";
import { XActionSlotSection } from "./XActionSlotSection";
import { XFactionRamps } from "./XFactionRamps";
import { useGame, useLobbyGame } from "~/app";
import { XFreighterRamp } from "../design/XFreighterRamp";

export const XActionSlots = () => {
  const { scale } = useGraphicScale();
  const { game } = useGame();
  const lobbyGame = useLobbyGame();
  const { useIxExtension = false } = getLobbyGameParams(lobbyGame) ?? {};
  const { actionSlots, players } = game;

  const sortByCost = (slots: ActionSlot[]) =>
    sortBy(slots, (slot) => slot.cost?.array.at(0)?.value ?? 0);
  const getSlotsByIcon = (agentIconChoice: AgentIcon) =>
    actionSlots.filter(({ agentIcon }) => agentIcon === agentIconChoice);

  const landsraadSlots = sortByCost(getSlotsByIcon(AgentIcon.Landsraad));
  const citySlots = getSlotsByIcon(AgentIcon.City);
  const spiceSlots = getSlotsByIcon(AgentIcon.SpiceTrade);
  const { passing: battleSpiceSlots, notPassing: noBattleSpiceSlots } =
    partition(spiceSlots, (slot) => Boolean(slot.isBattle));
  const { passing: leftLandsraadSlots, notPassing: rightLandsraadSlots } =
    partition(landsraadSlots, (_, index) => index < 2);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        transform: `scale(${scale})`,
        transformOrigin: "top left",
        width: "fit-content",
      }}
    >
      <XFactionRamps />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <XActionSlotSection
          agentIcon={AgentIcon.Landsraad}
          style={{
            width: "100%",
            display: "flex",
          }}
        >
          <div style={{ display: "flex", gap: Spacing.actions }}>
            {leftLandsraadSlots.map((actionSlot) => (
              <XActionSlotConnected
                key={actionSlot.id}
                actionSlot={actionSlot}
              />
            ))}
          </div>
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: 0.3,
            }}
          >
            <XThroneSvg width={80} />
          </div>
          <div style={{ display: "flex", gap: Spacing.actions }}>
            {rightLandsraadSlots.map((actionSlot) => (
              <XActionSlotConnected
                key={actionSlot.id}
                actionSlot={actionSlot}
              />
            ))}
          </div>
        </XActionSlotSection>
        <div style={{ display: "flex" }}>
          <XActionSlotGroup
            actionSlots={citySlots}
            groupBy={2}
            style={{
              borderBottomLeftRadius: 6,
            }}
          />
          <XActionSlotSection
            agentIcon={AgentIcon.SpiceTrade}
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "space-between",
              borderBottomRightRadius: 6,
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: Spacing.actions,
              }}
            >
              {noBattleSpiceSlots.map((slot) => (
                <XActionSlotConnected key={slot.id} actionSlot={slot} />
              ))}
            </div>
            {useIxExtension && (
              <XFreighterRamp
                levels={players.map((player) => ({
                  level: player.freighterLevel,
                  playerId: player.id,
                }))}
              />
            )}
            <XPackBy
              packSize={2}
              gap={Spacing.actions}
              lineStyle={{ justifyContent: "flex-end" }}
            >
              {battleSpiceSlots.map((slot) => (
                <XActionSlotConnected key={slot.id} actionSlot={slot} />
              ))}
            </XPackBy>
          </XActionSlotSection>
        </div>
      </div>
    </div>
  );
};

const XActionSlotConnected = ({ actionSlot }: { actionSlot: ActionSlot }) => {
  const { game } = useGame();
  const { onActionSlotClickFactory } = useActions();
  return (
    <XActionSlot
      actionSlot={actionSlot}
      onClick={onActionSlotClickFactory(actionSlot)}
      mentatOpt={game.mentatOnSlotOpt}
      allowOverlay={true}
      noShadow={true}
    />
  );
};
